import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchWithAuth, setCsrfToken } from './services/api';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const acceptSession = useCallback((data) => {
    setCsrfToken(data.csrf_token);
    setUser(data.user);
    setCatalog(data.catalog || []);
    setError('');
  }, []);
  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'same-origin', cache: 'no-store' });
      if (response.status === 401) { setUser(null); setCsrfToken(''); setError(''); return; }
      if (!response.ok) throw new Error('Unable to check access. Please retry.');
      acceptSession(await response.json());
    } catch (e) { setError(e.message); setUser(null); }
    finally { setLoading(false); }
  }, [acceptSession]);

  useEffect(() => {
    // One-time retirement of the old app-issued JWT storage.
    localStorage.removeItem('api_key');
    localStorage.removeItem('is_authenticated');
    refresh();
    const expired = () => { setUser(null); setCsrfToken(''); };
    window.addEventListener('auth-expired', expired);
    window.addEventListener('auth-forbidden', refresh);
    window.addEventListener('focus', refresh);
    const timer = setInterval(() => { if (!document.hidden) refresh(); }, 60000);
    return () => {
      clearInterval(timer);
      window.removeEventListener('auth-expired', expired);
      window.removeEventListener('auth-forbidden', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    let last = 0;
    const activity = () => {
      if (document.hidden || Date.now() - last < 60000) return;
      last = Date.now();
      fetchWithAuth('/api/auth/activity', { method: 'POST' }).catch(() => {});
    };
    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach(name => window.addEventListener(name, activity, { passive: true }));
    // Opening/reloading the application is activity; /me polling is not.
    activity();
    return () => events.forEach(name => window.removeEventListener(name, activity));
  }, [userId]);

  const logout = async () => {
    const response = await fetchWithAuth('/api/auth/logout', { method: 'POST' });
    if (!response.ok) throw new Error('Logout failed. Please retry.');
    setUser(null); setCsrfToken('');
  };
  return <AuthContext.Provider value={{
    user, catalog, loading, error, refresh, logout, acceptSession,
    isAuthenticated: Boolean(user), can: id => Boolean(user?.access?.[id]),
  }}>{children}</AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
